// A table as a file: one model, written as CSV or as an Excel workbook. The
// pages build an ExportTable; nothing here knows what the cells mean.

import { ExportOption } from "../../components/common/ExportMenu";
import { downloadBlob } from "./download";
import { exportFilename } from "./filename";

// What a cell holds; a list is written as its items joined by "; "
export type ExportCell =
  | string
  | number
  | Date
  | null
  | undefined
  | readonly string[];

export interface ExportColumn {
  header: string;
  // How the cell is typed in Excel; text when omitted
  kind?: "text" | "number" | "date" | "url";
  // Width in characters; estimated from the content when omitted
  width?: number;
}

export interface ExportTable {
  // The sheet's name
  name: string;
  columns: ExportColumn[];
  rows: ExportCell[][];
}

// What the About sheet says of the file
export interface ExportMeta {
  title: string;
  generatedAt: Date;
  source?: string;
  // Label and value of each filter in force
  filters?: readonly (readonly [string, string])[];
  note?: string;
}

const LIST_SEPARATOR = "; ";

export const CSV_MIME = "text/csv;charset=utf-8";
export const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

// The widest a column is estimated to need, in characters
const MAX_WIDTH = 60;
const MIN_WIDTH = 8;

// Excel's limit on a sheet's name, and the characters it refuses
const SHEET_NAME_LIMIT = 31;
const SHEET_NAME_ILLEGAL = /[[\]:*?/\\]/g;

const isoDay = (date: Date): string => date.toISOString().slice(0, 10);

// A cell as plain text, empty for nothing
const textOf = (cell: ExportCell): string => {
  if (cell === null || cell === undefined) {
    return "";
  }
  if (cell instanceof Date) {
    return isoDay(cell);
  }
  if (typeof cell === "string" || typeof cell === "number") {
    return String(cell);
  }
  return cell.join(LIST_SEPARATOR);
};

// A text a spreadsheet would run as a formula gets a quote before it
const FORMULA_START = /^[=+\-@\t\r]/;

const csvField = (cell: ExportCell): string => {
  let text = textOf(cell);
  if (typeof cell !== "number" && FORMULA_START.test(text)) {
    text = `'${text}`;
  }
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

// The table as CSV (RFC 4180): a header line, then a line per row
export const toCsv = (table: ExportTable): string =>
  [table.columns.map((column) => column.header), ...table.rows]
    .map((line) => `${line.map(csvField).join(",")}\r\n`)
    .join("");

// The CSV with the byte order mark Excel needs to read UTF-8
export const csvBlob = (table: ExportTable): Blob =>
  new Blob(["﻿" + toCsv(table)], { type: CSV_MIME });

const sheetName = (name: string, taken: Set<string>): string => {
  const base =
    name.replace(SHEET_NAME_ILLEGAL, "").trim().slice(0, SHEET_NAME_LIMIT) ||
    "Sheet";
  let candidate = base;
  for (let n = 2; taken.has(candidate.toLowerCase()); n += 1) {
    const suffix = ` (${n})`;
    candidate = base.slice(0, SHEET_NAME_LIMIT - suffix.length) + suffix;
  }
  taken.add(candidate.toLowerCase());
  return candidate;
};

const widthOf = (table: ExportTable, index: number): number => {
  const given = table.columns[index].width;
  if (given !== undefined) {
    return given;
  }
  const longest = Math.max(
    table.columns[index].header.length,
    ...table.rows.map((row) => textOf(row[index]).length),
  );
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, longest + 2));
};

/**
 * The tables as an Excel workbook: a sheet per table, then an About sheet.
 *
 * @param tables - The tables to write, one sheet each
 * @param meta - What the About sheet says of the file
 * @returns The workbook's bytes
 */
export const toXlsxBuffer = async (
  tables: ExportTable[],
  meta: ExportMeta,
): Promise<ArrayBuffer> => {
  // Loaded when a file is asked for, so it stays out of the main bundle
  const { Workbook } = await import("exceljs");
  const workbook = new Workbook();
  const taken = new Set<string>();

  for (const table of tables) {
    const sheet = workbook.addWorksheet(sheetName(table.name, taken), {
      views: [{ state: "frozen", ySplit: 1 }],
    });
    sheet.columns = table.columns.map((column, index) => ({
      header: column.header,
      width: widthOf(table, index),
    }));
    sheet.getRow(1).font = { bold: true };
    for (const row of table.rows) {
      const added = sheet.addRow([]);
      table.columns.forEach((column, index) => {
        const cell = added.getCell(index + 1);
        const value = row[index];
        if (value === null || value === undefined || value === "") {
          return;
        }
        if (Array.isArray(value)) {
          cell.value = value.join(LIST_SEPARATOR);
          cell.alignment = { wrapText: true, vertical: "top" };
        } else if (value instanceof Date) {
          cell.value = value;
          cell.numFmt = "yyyy-mm-dd";
        } else if (typeof value === "number") {
          cell.value = value;
        } else if (column.kind === "url" && typeof value === "string") {
          cell.value = { text: value, hyperlink: value };
        } else {
          cell.value = textOf(value);
        }
      });
    }
    if (table.columns.length > 0) {
      sheet.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: 1, column: table.columns.length },
      };
    }
  }

  const about = workbook.addWorksheet(sheetName("About", taken));
  about.columns = [{ width: 18 }, { width: MAX_WIDTH }];
  const lines: [string, string][] = [
    ["Title", meta.title],
    ["Generated", meta.generatedAt.toISOString()],
    ...(meta.source ? [["Source", meta.source] as [string, string]] : []),
    ...(meta.filters ?? []).map(([label, value]): [string, string] => [
      label,
      value,
    ]),
    ...(meta.note ? [["Note", meta.note] as [string, string]] : []),
  ];
  for (const line of lines) {
    about.addRow(line);
  }
  about.getColumn(1).font = { bold: true };
  about.getColumn(2).alignment = { wrapText: true, vertical: "top" };

  return (await workbook.xlsx.writeBuffer()) as ArrayBuffer;
};

// The tables as an Excel file
export const xlsxBlob = async (
  tables: ExportTable[],
  meta: ExportMeta,
): Promise<Blob> =>
  new Blob([await toXlsxBuffer(tables, meta)], { type: XLSX_MIME });

// What an export is made of, built when the file is asked for
export interface ExportBuild {
  tables: ExportTable[];
  meta: ExportMeta;
  // The parts of the file name, before the day and the extension
  filename: readonly (string | null | undefined)[];
}

// The two standard options of a table: Excel and CSV (the first table).
// `build` runs on the click, so it may fetch what the file holds.
export type TableFormat = "xlsx" | "csv";

export const TABLE_FORMAT_LABELS: Record<TableFormat, string> = {
  xlsx: "Excel (.xlsx)",
  csv: "CSV",
};

// Builds the tables and saves them in the format asked: a workbook with an
// About sheet, or the first table as CSV
export const writeTableExport = async (
  format: TableFormat,
  build: () => ExportBuild | Promise<ExportBuild>,
): Promise<void> => {
  const { tables, meta, filename } = await build();
  if (format === "xlsx") {
    downloadBlob(exportFilename(filename, "xlsx"), await xlsxBlob(tables, meta));
  } else {
    downloadBlob(exportFilename(filename, "csv"), csvBlob(tables[0]));
  }
};

export const tableExportOptions = (
  build: () => ExportBuild | Promise<ExportBuild>,
): ExportOption[] =>
  (["xlsx", "csv"] as const).map((format) => ({
    key: format,
    label: TABLE_FORMAT_LABELS[format],
    run: () => writeTableExport(format, build),
  }));
